import { useMemo } from "react";
import type { RootState } from "~/app/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Link } from "~/common/components/Link";
import type { NavigationNode } from "~/domains/navigation/types/NavigationTypes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { ChildCategoryNavigationCarousel } from "~/features/categoryNavigation/components/ChildCategoryNavigationCarousel";
import { Stack } from "~/common/mui";

interface TreeNode extends NavigationNode {
  childNodes: TreeNode[];
}

export interface NodeResponse {
  parentNode: NavigationNode | null;
  childNodes: TreeNode[];
}

type TileData = {
  title: string;
  imageUrl: string;
  url?: string;
  onClick?: () => void;
};

export const ChildCategoryNavigation = () => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const navigationNodes = useSelector(selectNavigationNodes);
  const rootNode = useSelector(selectRootNode);
  const productGroupName = extractProductGroupNameFromUrl();

  const { tileData, parentNode }: { tileData: TileData[]; parentNode: NavigationNode | null } = useMemo(() => {
    const resultNode = getProductGroupChildCategories(rootNode, navigationNodes, productGroupName);
    return {
      parentNode: resultNode.parentNode,
      tileData: resultNode.childNodes.map((node) => ({
        title: node.title,
        imageUrl: node.bigImage?.url ?? resultNode.parentNode?.bigImage?.url ?? "",
        url: node.url,
      })),
    };
  }, [navigationNodes, rootNode, productGroupName]);

  if (!tileData || tileData.length === 0) {
    return;
  }

  return (
    <Stack
      gap={2}
      sx={{ "--arrow-offset": "-20px", "--right-arrow-offset": "-20px" }}
      tracking-aux-sub-category-marker="true"
    >
      {isDesktop && parentNode && (
        <Link href={parentNode.url} iconBefore={<Icon type={IconType.ChevronLeft} />}>
          {t(parentNode.title)}
        </Link>
      )}
      <ChildCategoryNavigationCarousel items={tileData} />
    </Stack>
  );
};

const selectNavigationNodes = (state: RootState) => {
  return state.navigation.categoryNavigation.nodes;
};
const selectRootNode = (state: RootState) => {
  return state.navigation.categoryNavigation.root;
};

const extractProductGroupNameFromUrl = () => {
  const pathName = window.location.pathname;
  const parts = pathName.split("/");
  return parts[parts.length - 1];
};

export function getProductGroupChildCategories(
  root: NavigationNode,
  nodes: Record<number, NavigationNode>,
  prodGroupName: string,
) {
  const buildNode = (node: NavigationNode): TreeNode => {
    return {
      ...node,
      childNodes: node.children
        .map((childId) => nodes[childId])
        .filter((childNode) => childNode !== undefined)
        .filter((childNode) => childNode.level <= 4)
        .map((childNode) => buildNode(childNode)),
    };
  };

  const findTargetAndParentNode = (
    currentNode: NavigationNode,
    groupName: string,
    parentNode: NavigationNode | null = null,
  ): { parentNode: NavigationNode | null; childNodes: NavigationNode[] } | null => {
    const basePath = currentNode?.url && currentNode?.url.split("?")[0];
    const pathArray = basePath ? basePath.split("/") : [];
    if (currentNode.url && pathArray[pathArray.length - 1] === groupName) {
      if (parentNode) {
        return {
          parentNode,
          childNodes: currentNode.children
            .map((childId) => nodes[childId])
            .filter((childNode) => childNode?.pimCategoryNode),
        };
      }
      return { parentNode: null, childNodes: [] };
    }
    for (const childId of currentNode.children) {
      const childNode = nodes[childId];
      if (childNode) {
        const response = findTargetAndParentNode(childNode, groupName, currentNode);
        if (response) {
          return response;
        }
      }
    }

    return null;
  };

  const result = findTargetAndParentNode(root, prodGroupName);
  if (!result || !result.childNodes) {
    return { parentNode: null, childNodes: [] };
  }

  const childNodeList = result.childNodes.map((node) => buildNode(node));
  return {
    ...result,
    childNodes: childNodeList,
  };
}
